import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../utils/cx.js";

export interface PaginationProps {
  total: number;
  pageSize: number;
  current: number;
  onChange: (page: number) => void;
  /** Show the page-size selector next to the pager. */
  showSizeChanger?: boolean;
  /** Selectable page sizes (only meaningful with `showSizeChanger`). */
  pageSizeOptions?: number[];
  /** Fires when the user picks a different page size. */
  onPageSizeChange?: (size: number) => void;
  siblingCount?: number;
  className?: string;
}

function pageWindow(current: number, count: number, siblings: number): (number | "…")[] {
  const range: (number | "…")[] = [];
  const start = Math.max(2, current - siblings);
  const end = Math.min(count - 1, current + siblings);
  range.push(1);
  if (start > 2) range.push("…");
  for (let p = start; p <= end; p++) range.push(p);
  if (end < count - 1) range.push("…");
  if (count > 1) range.push(count);
  return range;
}

export function Pagination({
  total,
  pageSize,
  current,
  onChange,
  showSizeChanger = false,
  pageSizeOptions,
  onPageSizeChange,
  siblingCount = 1,
  className,
}: PaginationProps) {
  const count = Math.max(1, Math.ceil(total / pageSize));
  const cell =
    "inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-transparent px-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40";

  return (
    <nav className={cn("flex items-center gap-1", className)} aria-label="Sayfalama">
      <button
        type="button"
        className={cell}
        disabled={current <= 1}
        onClick={() => onChange(current - 1)}
        aria-label="Önceki"
      >
        <ChevronLeft className="size-4" />
      </button>
      {pageWindow(current, count, siblingCount).map((p, i) =>
        p === "…" ? (
          <span key={`gap-${i}`} className="px-1 text-muted-foreground">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            aria-current={p === current || undefined}
            className={cn(
              cell,
              p === current &&
                "border-border bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
            )}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        ),
      )}
      <button
        type="button"
        className={cell}
        disabled={current >= count}
        onClick={() => onChange(current + 1)}
        aria-label="Sonraki"
      >
        <ChevronRight className="size-4" />
      </button>
      {showSizeChanger && pageSizeOptions != null && (
        <select
          className="ml-2 h-9 rounded-md border border-input bg-transparent px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:bg-input/30"
          value={pageSize}
          onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          aria-label="Sayfa boyutu"
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>
              {s} / sayfa
            </option>
          ))}
        </select>
      )}
    </nav>
  );
}
